import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "../lib/api";
import { Ornament } from "../components/Ornament";

/**
 * Handles both /subscribe/success and /gift/success — endpoint same status payload.
 */
export default function SubscribeSuccess({ kind = "subscription" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState("checking");
  const [details, setDetails] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const session_id = params.get("session_id");
    if (!session_id) { setState("failed"); return; }

    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${session_id}`);
        setDetails(res.data);
        if (res.data?.payment_status === "paid") { setState("paid"); return; }
        if (res.data?.status === "expired") { setState("expired"); return; }
        if (attempts.current >= 8) { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts.current >= 8) setState("failed");
        else setTimeout(poll, 2500);
      }
    };
    poll();
  }, [location.search]);

  const isGift = kind === "gift" || details?.kind === "gift";

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#111D33" }} data-testid={`${kind}-success-page`}>
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-xl w-full bg-[#FAF7F2] p-10 sm:p-12 text-center" style={{ borderRadius: 4 }}>
          {state === "checking" && (
            <>
              <Loader2 className="animate-spin mx-auto text-[#B8973A]" size={28} />
              <p className="mt-6 italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>
                Confirming…
              </p>
            </>
          )}
          {state === "paid" && (
            <>
              <p className="text-[#B8973A] text-3xl mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</p>
              <p className="label-ui text-[11px] text-[#B8973A] mb-4">Thank you</p>
              <h1 className="text-4xl sm:text-5xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                {isGift ? "Your gift is on its way." : "Welcome to The Living Book."}
              </h1>
              <Ornament className="my-8" />
              <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem", lineHeight: 1.55 }}>
                {isGift
                  ? `We've recorded the gift for ${details?.recipient_email}. They'll receive their code shortly.`
                  : "Every chapter is now open, every voice will be preserved. Begin whenever you're ready."}
              </p>
              <button onClick={() => navigate("/dashboard")} className="btn-primary mt-10" data-testid="back-to-dashboard-btn">
                Back to your family
              </button>
            </>
          )}
          {(state === "expired" || state === "failed") && (
            <>
              <h1 className="text-3xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                {state === "expired" ? "The payment session has expired." : "We couldn't confirm your payment."}
              </h1>
              <p className="mt-4 italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Your responses are still in your account. Please try again.
              </p>
              <button onClick={() => navigate("/subscribe")} className="btn-primary mt-8" data-testid="retry-subscribe-btn">Try again</button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
